import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';

export type ApiKeyFormData = {
    name: string;
    apiKey: string;
    url: string;
    projectId?: string;
};

export type ApiKeyFormErrors = Partial<Record<keyof ApiKeyFormData, string>>;

function isValidHttpUrl(value: string): boolean {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

export function validateWatsonApiKeyForm(formData: ApiKeyFormData): ApiKeyFormErrors {
    const errors: ApiKeyFormErrors = {};
    const name = formData.name?.trim() ?? '';
    const apiKey = formData.apiKey?.trim() ?? '';
    const url = formData.url?.trim() ?? '';
    const projectId = formData.projectId?.trim() ?? '';

    if (!name) {
        errors.name = 'Name is required.';
    } else if (name.length < 2) {
        errors.name = 'Name must be at least 2 characters.';
    }

    if (!apiKey) {
        errors.apiKey = 'API Key is required.';
    }

    if (!url) {
        errors.url = 'API URL is required.';
    } else if (!isValidHttpUrl(url)) {
        errors.url = 'Enter a valid URL starting with http:// or https://.';
    }

    if (!projectId) {
        errors.projectId = 'Project ID is required.';
    }

    return errors;
}

interface WatsonApiKeysProps {
    formData: ApiKeyFormData;
    setFormData: React.Dispatch<React.SetStateAction<ApiKeyFormData>>;
    errors?: ApiKeyFormErrors;
    onClearError?: (field: keyof ApiKeyFormData) => void;
}

export default function WatsonApiKeysForm({
    setFormData,
    formData,
    errors = {},
    onClearError,
}: WatsonApiKeysProps): React.ReactElement {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
            name,
            value
        } = e.target;

        if (onClearError && name in formData) {
            onClearError(name as keyof ApiKeyFormData);
        }

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    return (
        <div>
            <div className="space-y-2 mt-2">
                <Label htmlFor='name'> Name </Label>
                <Input
                    id={'name'}
                    name={'name'}
                    placeholder={'Enter Watson API key name'}
                    value={formData.name}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.name)}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
            <div className="space-y-2 mt-2">
                <Label htmlFor='apiKey'> API Key </Label>
                <Input
                    id={'apiKey'}
                    name={'apiKey'}
                    placeholder={'Enter Watson API key'}
                    value={formData.apiKey}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.apiKey)}
                />
                {errors.apiKey && <p className="text-sm text-destructive">{errors.apiKey}</p>}
            </div>
            <div className="space-y-2 mt-2">
                <Label htmlFor='url'> API URL </Label>
                <Input
                    id={'url'}
                    name={'url'}
                    placeholder={'https://us-south.ml.cloud.ibm.com'}
                    value={formData.url}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.url)}
                />
                {errors.url && <p className="text-sm text-destructive">{errors.url}</p>}
            </div>
            <div className="space-y-2 mt-2">
                <Label htmlFor='projectId'> Project ID </Label>
                <Input
                    id={'projectId'}
                    name={'projectId'}
                    placeholder={'Enter Watson Project ID'}
                    value={formData.projectId}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.projectId)}
                />
                {errors.projectId && <p className="text-sm text-destructive">{errors.projectId}</p>}
            </div>

        </div>
    );
}
